/* Copyright (c) 2017-2021 VMware, Inc. All rights reserved. VMware Confidential */

import { Injectable, OnDestroy } from "@angular/core";
import { WindowService } from "src/app/services/responsive-ui/window.service";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class ResponsiveUiService implements OnDestroy {
    private static readonly XS_BREAKPOINT: number = 479;
    private static readonly M_BREAKPOINT: number = 992;
    private static readonly L_BREAKPOINT: number = 1200;
    private static readonly XL_BREAKPOINT: number = 1400;
    private static readonly M_VERTICAL_BREAKPOINT: number = 767;

    public breakPoint!: BreakPoint;
    public breakPointVertical!: BreakPointVertical;

    private _breakPointChanged: Subject<void> = new Subject<void>();
    public onBreakPointChanged: Observable<void> = this._breakPointChanged.asObservable();

    private _unsubscribeSubject: Subject<void> = new ReplaySubject<void>(1);

    constructor(windowService: WindowService) {
        windowService.onWindowResized.pipe(
            takeUntil(this._unsubscribeSubject)).subscribe(() => this.calcBreakPoint());
    }

    public init(): void {
        setTimeout(() => this.calcBreakPoint());
    }

    public destroy(): void {
        this._unsubscribeSubject.next();
        this._unsubscribeSubject.complete();

        this._breakPointChanged.next();
        this._breakPointChanged.complete();
    }

    public isExtraSmall(): boolean {
        return this.breakPoint == BreakPoint.XS;
    }

    public isSmall(): boolean {
        return this.breakPoint == BreakPoint.S;
    }

    public isExtraSmallToSmall(): boolean {
        return this.isExtraSmall() || this.isSmall();
    }

    public isMedium(): boolean {
        return this.breakPoint == BreakPoint.M;
    }

    public isLarge(): boolean {
        return this.breakPoint == BreakPoint.L;
    }

    public isExtraLarge(): boolean {
        return this.breakPoint == BreakPoint.XL;
    }

    public isMediumToLarge(): boolean {
        return this.isMedium() || this.isLarge();
    }

    public isMediumToExtraLarge(): boolean {
        return this.isMedium() || this.isLarge() || this.isExtraLarge();
    }

    public isLargeToExtraLarge(): boolean {
        return this.isLarge() || this.isExtraLarge();
    }

    public isSmallVertical(): boolean {
        return this.breakPointVertical == BreakPointVertical.S;
    }

    public isMediumVertical(): boolean {
        return this.breakPointVertical == BreakPointVertical.M;
    }

    public isMediumToExtraLargeVertical(): boolean {
        return this.isMediumVertical();
    }

    public isInitialized(): boolean {
        return this.breakPoint != null;
    }

    ngOnDestroy(): void {
        this._unsubscribeSubject.next();
        this._unsubscribeSubject.complete();
    }

    private calcBreakPoint(): void {
        const storeBreakPoint: BreakPoint = this.breakPoint;
        const storeBreakPointVertical: BreakPointVertical = this.breakPointVertical;
        setTimeout(() => {
            if (this.isExtraLargeScreen()) {
                this.breakPoint = BreakPoint.XL;
            } else if (this.isLargeScreen()) {
                this.breakPoint = BreakPoint.L;
            } else if (this.isMediumScreen()) {
                this.breakPoint = BreakPoint.M;
            } else if (this.isSmallScreen()) {
                this.breakPoint = BreakPoint.S;
            } else {
                this.breakPoint = BreakPoint.XS;
            }
            if (this.isMediumVerticalScreen()) {
                this.breakPointVertical = BreakPointVertical.M;
            } else {
                this.breakPointVertical = BreakPointVertical.S;
            }
            if (storeBreakPoint != this.breakPoint || storeBreakPointVertical != this.breakPointVertical) {
                this._breakPointChanged.next();
            }
        });
    }

    private isSmallScreen(): boolean {
        return window.innerWidth > ResponsiveUiService.XS_BREAKPOINT;
    }

    private isMediumScreen(): boolean {
        return window.innerWidth > ResponsiveUiService.M_BREAKPOINT;
    }

    private isLargeScreen(): boolean {
        return window.innerWidth > ResponsiveUiService.L_BREAKPOINT;
    }

    private isExtraLargeScreen(): boolean {
        return window.innerWidth > ResponsiveUiService.XL_BREAKPOINT;
    }

    private isMediumVerticalScreen(): boolean {
        return window.innerHeight > ResponsiveUiService.M_VERTICAL_BREAKPOINT;
    }
}

export enum BreakPoint {
    XS,
    S,
    M,
    L,
    XL
}

export enum BreakPointVertical {
    S,
    M
}
